import { useState, useRef, useEffect, Fragment } from 'react'
import type { IndicatorStyleOverrides, LegendValueFormat } from '@edge/charts-indicators'
import type { IndicatorFigureValue } from '../hooks/useIndicatorValues'
import { laneBand, laneBandsVariable } from '../utils/lane-geometry'
import type { TimeframeVisibilityRule } from '../utils/timeframeVisibility'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ActiveIndicator {
  key: string
  /** Indicator type / base name — drives label, catalog def, persistence. */
  name: string
  /** klinecharts registered instance name. Differs from `name` only for a
   *  duplicated MA-family overlay (e.g. a second EMA → `EMA#2`). All imperative
   *  chart calls (create/update/remove/value lookup) must use this; defaults to
   *  `name` for the first/only instance and for non-duplicable indicators. */
  instanceName?: string
  label: string
  paneId: string
  paneType: 'main' | 'sub'
  calcParams?: unknown[]
  visible: boolean
  /** Per-figure style overrides keyed by figure key */
  styles?: IndicatorStyleOverrides
  /** Pane placement for indicators that can move between the price pane and
   *  their own sub-pane (currently just VOL). 'main' = overlaid on the price
   *  pane, 'sub' = own bottom pane. Undefined = not movable. */
  placement?: 'main' | 'sub'
  /** Per-timeframe visibility rule (the "Visibility" tab). Undefined ⇒
   *  visible on all timeframes. Persisted; edited from the settings drawer. */
  timeframeVisibility?: TimeframeVisibilityRule
  /** Higher timeframe the indicator is COMPUTED from (MTF, e.g. '1H').
   *  Undefined ⇒ the chart's own timeframe. Persisted; MTF-eligible
   *  indicators only. Distinct from `timeframeVisibility` (where it SHOWS). */
  sourceTimeframe?: string
  /** Persisted pane height as a fraction of the chart container height (0..1).
   *  Sub-pane indicators only; drives restore-on-load and drag capture. */
  paneHeightRatio?: number
  /** Derived (not persisted): the eye is on but the current period is excluded
   *  by `timeframeVisibility`, so the indicator isn't drawing right now. Drives
   *  the "hidden on this timeframe" affordance in the legend row. */
  hiddenByTimeframe?: boolean
  /** Suppress the settings gear for this row (external indicators without a
   *  settings callback — they have no calcParams to edit either). */
  hideSettings?: boolean
  /** Suppress the remove button for this row (read-only external indicators). */
  hideRemove?: boolean
  /** The remove button's tooltip when it does more than "Remove" (ExternalIndicatorEntry). */
  removeTitle?: string
  /** Show the locate (crosshair) button — external indicators that can focus
   *  their source (e.g. the debugged node in the backtest editor). */
  locatable?: boolean
  /** How each figure's legend value prints, by figure key (a script's Pine format,
   *  precision and display); absent keeps the compact readout. */
  valueFormats?: Record<string, LegendValueFormat>
  /** A figure's color on one bar, when it changes per bar (ExternalIndicatorEntry). */
  figureColorAt?: (figureKey: string, timestamp: number) => string | undefined
  /** Show the focus (pin-to-price) button — external sub-pane indicators that
   *  can mirror onto the main price chart while keeping their own pane (e.g. a
   *  debug condition's highlight band in the backtest editor). */
  focusable?: boolean
  /** Active state of the focus button — whether the row is currently pinned to
   *  the price chart. */
  isFocused?: boolean
  /** Drawn on the candles while it lives in a lower pane (a backtest Debug Chart
   *  condition: its lane stays below, and it also shades the candles). The move
   *  arrow then points down, since the only move left is to keep it below. */
  onCandles?: boolean
  /** Lane assignment for a multi-lane "strip" pane (e.g. the boolean lane-strip
   *  in the backtest debug chart). When set, the legend anchors this row at its
   *  lane's vertical band instead of the pane's top-left. Requires the legend to
   *  receive the pane's pixel height. */
  lane?: { index: number; count: number; visible?: boolean[]; collapsedPx?: number }
  /** Derived (not persisted): the host is still computing this row's values (an
   *  external indicator's evaluation in flight). Drives the spinner after the name. */
  loading?: boolean
}

export interface IndicatorLegendProps {
  indicators: ActiveIndicator[]
  indicatorValues: Map<string, IndicatorFigureValue[]>
  onToggleVisibility: (key: string) => void
  onRemove: (key: string) => void
  onUpdateParams: (key: string, newParams: unknown[]) => void
  /** Open the rich settings drawer (P2). When provided, replaces the inline param
   *  popover. `anchor` is the viewport point of the gear click, so the host can
   *  open the drawer beside the click instead of centered over the chart. */
  onOpenSettings?: (key: string, anchor?: { x: number; y: number }) => void
  /** Move a placement-capable indicator (VOL) between the price pane and its
   *  own sub-pane. The arrow button only renders when this is provided and
   *  the indicator carries a `placement`. */
  onMovePane?: (key: string) => void
  /** Locate the indicator's source (crosshair button). Only renders when this
   *  is provided and the row is marked `locatable`. */
  onLocate?: (key: string) => void
  /** Toggle "focus on price" (pin button) for a row. Only renders when this is
   *  provided and the row is marked `focusable`; the button reflects the row's
   *  `isFocused` active state. */
  onToggleFocus?: (key: string) => void
  /** Restrict to a specific paneType — used to mount the main-pane legend */
  paneType?: 'main' | 'sub'
  /** Restrict to a single paneId. Takes precedence over paneType. Used to mount
   *  one legend per sub-pane, anchored inside that pane. */
  paneId?: string
  /** Explicit top offset (px) for the legend wrapper. Overrides the paneType default. */
  top?: number
  /** Explicit left offset (px, default 4) — used to clear the floating drawing bar. */
  left?: number
  /** Pane pixel height — required to anchor laned rows (the boolean lane-strip).
   *  When omitted, laned rows fall back to the normal flex stack. */
  paneHeight?: number
  /** When true, the rows are hidden and only the expander chevron shows.
   *  Requires `onToggleCollapse`. Used for the price-pane legend. */
  collapsed?: boolean
  /** When provided, renders a minimize/expand chevron below
   *  the rows (price pane only). Omit on sub-panes to hide the control. */
  onToggleCollapse?: () => void
}

/* ------------------------------------------------------------------ */
/*  IndicatorLegend                                                    */
/* ------------------------------------------------------------------ */

export function IndicatorLegend({
  indicators,
  indicatorValues,
  onToggleVisibility,
  onRemove,
  onUpdateParams,
  onOpenSettings,
  onMovePane,
  onLocate,
  onToggleFocus,
  paneType = 'main',
  paneId,
  top,
  left,
  paneHeight,
  collapsed = false,
  onToggleCollapse,
}: IndicatorLegendProps) {
  const filtered = indicators.filter((ind) =>
    paneId ? ind.paneId === paneId : ind.paneType === paneType,
  )
  if (filtered.length === 0) return null

  const wrapperTop = top ?? (paneType === 'main' ? 24 : 4)
  // A "laned" pane (the boolean lane-strip) positions each row at its lane's
  // vertical band instead of flex-stacking them at the pane top. Gated on
  // paneHeight so non-strip panes are completely unaffected.
  const laned = paneHeight != null && filtered.some((ind) => ind.lane)
  // The minimize chevron only makes sense in the normal flex stack (price pane);
  // a laned strip pane never receives onToggleCollapse.
  const showCollapseToggle = onToggleCollapse != null && !laned

  return (
    <div
      data-testid="indicator-legend"
      style={{ top: wrapperTop, left: left ?? 4 }}
      className={'pointer-events-none absolute z-10 ' + (laned ? '' : 'flex flex-col gap-px')}
    >
      {!collapsed &&
        filtered.map((ind) => {
          const row = (
            <IndicatorRow
              indicator={ind}
              figureValues={indicatorValues.get(ind.key)}
              onToggleVisibility={() => onToggleVisibility(ind.key)}
              onRemove={() => onRemove(ind.key)}
              onUpdateParams={(params) => onUpdateParams(ind.key, params)}
              onOpenSettings={
                onOpenSettings
                  ? (e) => onOpenSettings(ind.key, { x: e.clientX, y: e.clientY })
                  : undefined
              }
              onMovePane={onMovePane && ind.placement ? () => onMovePane(ind.key) : undefined}
              onLocate={onLocate && ind.locatable ? () => onLocate(ind.key) : undefined}
              onToggleFocus={
                onToggleFocus && ind.focusable ? () => onToggleFocus(ind.key) : undefined
              }
            />
          )
          // Laned: wrap each row in an absolutely-positioned lane band. The wrapper
          // is pointer-events-none; IndicatorRow re-enables pointer events on itself
          // so its buttons stay clickable.
          // Laned pane: every row gets an absolutely-positioned wrapper at its lane
          // band. A laneless row on a laned pane (not a real case today) pins to the
          // top rather than silently relying on flow the absolute container lacks.
          if (laned) {
            // Variable lane heights when the row carries a per-lane visibility
            // pattern (hidden lanes collapsed) — same geometry the strip draw
            // uses, so the legend row sits exactly on its (possibly collapsed)
            // lane. Falls back to equal bands when no pattern is supplied.
            const laneTop =
              ind.lane && paneHeight != null
                ? ind.lane.visible
                  ? (laneBandsVariable(
                      ind.lane.visible,
                      paneHeight,
                      ind.lane.collapsedPx ?? paneHeight / ind.lane.count,
                    )[ind.lane.index]?.top ?? 0)
                  : laneBand(ind.lane.index, ind.lane.count, paneHeight).top
                : 0
            return (
              <div
                key={ind.key}
                data-lane-wrapper=""
                className="pointer-events-none absolute"
                style={{ top: laneTop, left: 0 }}
              >
                {row}
              </div>
            )
          }
          // Non-laned: keyed Fragment renders no DOM node, so the flex stack is
          // byte-identical to the pre-lane layout.
          return <Fragment key={ind.key}>{row}</Fragment>
        })}
      {showCollapseToggle && (
        <CollapseToggle collapsed={collapsed} count={filtered.length} onClick={onToggleCollapse} />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  CollapseToggle                                                      */
/* ------------------------------------------------------------------ */

/** Minimize/expand control sitting below the legend rows. Two deliberate looks:
 *
 *  - EXPANDED: the original minimalist bare up-chevron — the rows above it
 *    already signal what lives here, so no chip chrome is added.
 *  - COLLAPSED: a chip (standard chart-chrome background, same treatment as
 *    the settings gear / drill chips) with a down-chevron + the count of
 *    hidden legend rows. When collapsed this button is the ONLY trace of the
 *    legend, so it must survive a noisy candle background and answer "where
 *    did my indicators go?" at a glance. Count includes eye-off /
 *    timeframe-hidden rows — expanding brings them all back.
 */
function CollapseToggle({
  collapsed,
  count,
  onClick,
}: {
  collapsed: boolean
  count: number
  onClick: () => void
}) {
  if (collapsed) {
    return (
      <button
        title="Show indicators"
        aria-pressed={true}
        onClick={onClick}
        className="pointer-events-auto flex h-[18px] items-center gap-0.5 self-start rounded border border-[hsl(var(--chart-text)/0.15)] bg-[hsl(var(--chart-canvas)/0.9)] px-1 py-0 text-[10px] font-medium text-[hsl(var(--chart-text-muted))] backdrop-blur-sm transition-colors hover:text-[hsl(var(--chart-text))]"
      >
        <ChevronDownIcon />
        {count}
      </button>
    )
  }
  return (
    <button
      title="Minimize indicators"
      aria-pressed={false}
      onClick={onClick}
      className="pointer-events-auto flex h-[14px] w-5 items-center justify-center self-start rounded-sm p-0 text-[hsl(var(--chart-text-muted))] opacity-60 transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))] hover:opacity-100"
    >
      <ChevronUpIcon />
    </button>
  )
}

function ChevronUpIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 15 12 9 18 15" />
    </svg>
  )
}

function ChevronDownIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/*  IndicatorRow                                                       */
/* ------------------------------------------------------------------ */

function IndicatorRow({
  indicator,
  figureValues,
  onToggleVisibility,
  onRemove,
  onUpdateParams,
  onOpenSettings,
  onMovePane,
  onLocate,
  onToggleFocus,
}: {
  indicator: ActiveIndicator
  figureValues?: IndicatorFigureValue[]
  onToggleVisibility: () => void
  onRemove: () => void
  onUpdateParams: (params: unknown[]) => void
  onOpenSettings?: (e: React.MouseEvent) => void
  onMovePane?: () => void
  onLocate?: () => void
  onToggleFocus?: () => void
}) {
  const [hover, setHover] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  return (
    <div
      aria-busy={indicator.loading || undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false)
        setSettingsOpen(false)
      }}
      className={
        'pointer-events-auto relative flex h-5 items-center gap-1 rounded-sm px-1 transition-colors ' +
        // Hovered: a flat, opaque surface (no alpha, no blur) so the candles behind
        // never show through the action icons. A hidden row's fade is state, but it
        // lifts on hover so its eye button stays readable. The inset ring adds the
        // hairline without changing the row's size.
        (hover
          ? 'bg-popover ring-1 ring-inset ring-primary/15'
          : indicator.visible && !indicator.hiddenByTimeframe
            ? ''
            : 'opacity-40')
      }
    >
      {/* Label - always visible. Capped + truncated so a long condition name
          (e.g. "BAR-SIZE > BAR-SIZE[-1][-1]") can't push the hover action
          cluster off the right edge of a narrow sub-pane; the full name stays
          discoverable via the title tooltip. */}
      <span
        title={indicator.label}
        className={
          'max-w-[140px] select-none truncate text-[11px] font-normal ' +
          (hover ? 'text-[hsl(var(--chart-text))]' : 'text-[hsl(var(--chart-text-muted))]')
        }
      >
        {indicator.label}
      </span>

      {/* Loading cue: the host is still computing this row (a script evaluation in
          flight). Stays through hover, like the timeframe cue below. */}
      {indicator.loading && (
        <span
          role="status"
          title="Loading"
          aria-label="Loading"
          className="flex shrink-0 items-center text-[hsl(var(--chart-text-muted))]"
        >
          <LoaderIcon />
        </span>
      )}

      {/* Hidden-on-this-timeframe cue: the eye is on but the visibility rule
          excludes the current period, so nothing is drawing right now. */}
      {indicator.hiddenByTimeframe && indicator.visible && (
        <span
          title="Hidden on this timeframe by its visibility rule"
          aria-label="Hidden on this timeframe"
          className="flex shrink-0 items-center text-[hsl(var(--chart-text-muted))]"
        >
          <svg
            width={11}
            height={11}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <polyline points="12 7 12 12 15 14" />
          </svg>
        </span>
      )}

      {hover ? (
        /* Hover state: action icons */
        <>
          <LegendButton
            title={indicator.visible ? 'Hide' : 'Show'}
            pressed={indicator.visible}
            onClick={onToggleVisibility}
          >
            {indicator.visible ? <EyeIcon /> : <EyeOffIcon />}
          </LegendButton>
          {onMovePane && (
            <LegendButton
              title={
                indicator.placement === 'main'
                  ? 'Show below chart'
                  : indicator.placement === 'sub' && indicator.onCandles
                    ? 'Show below chart only'
                    : 'Show on candles'
              }
              onClick={onMovePane}
            >
              {indicator.placement === 'main' ||
              (indicator.placement === 'sub' && indicator.onCandles) ? (
                <ArrowDownIcon />
              ) : (
                <ArrowUpIcon />
              )}
            </LegendButton>
          )}
          {onToggleFocus && (
            <LegendButton
              // A condition below the chart also paints on the candles; a row
              // with no pane to move (a Debug Values ruler) is only highlighted.
              title={
                indicator.placement === 'sub'
                  ? indicator.isFocused
                    ? 'Show below chart only'
                    : 'Also show on candles'
                  : indicator.isFocused
                    ? 'Remove highlight'
                    : 'Highlight line'
              }
              active={indicator.isFocused}
              pressed={indicator.isFocused}
              onClick={onToggleFocus}
            >
              <PinIcon filled={indicator.isFocused} />
            </LegendButton>
          )}
          {onLocate && (
            <LegendButton title="Show source node" onClick={onLocate}>
              <LocateIcon />
            </LegendButton>
          )}
          {!indicator.hideSettings &&
            (onOpenSettings || (indicator.calcParams && indicator.calcParams.length > 0)) && (
              <LegendButton
                title="Settings"
                onClick={(e) => {
                  if (onOpenSettings) {
                    onOpenSettings(e)
                  } else {
                    setSettingsOpen((v) => !v)
                  }
                }}
              >
                <GearIcon />
              </LegendButton>
            )}
          {!indicator.hideRemove && (
            <LegendButton title={indicator.removeTitle ?? 'Remove'} onClick={onRemove}>
              <CloseIcon />
            </LegendButton>
          )}
        </>
      ) : (
        /* Normal state: colored figure values */
        figureValues &&
        figureValues.map((fv, i) => (
          <span
            key={i}
            style={{ color: fv.color }}
            className="select-none whitespace-nowrap text-[11px] font-normal"
          >
            {fv.value}
          </span>
        ))
      )}

      {/* Settings popover */}
      {settingsOpen && indicator.calcParams && (
        <SettingsPopover
          calcParams={indicator.calcParams}
          onApply={(params) => {
            onUpdateParams(params)
            setSettingsOpen(false)
          }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  SettingsPopover                                                     */
/* ------------------------------------------------------------------ */

function SettingsPopover({
  calcParams,
  onApply,
}: {
  calcParams: unknown[]
  onApply: (params: unknown[]) => void
}) {
  const [localParams, setLocalParams] = useState<string[]>(calcParams.map((p) => String(p)))
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLocalParams(calcParams.map((p) => String(p)))
  }, [calcParams])

  const handleApply = () => {
    const parsed = localParams.map((p) => {
      const n = Number(p)
      return isNaN(n) ? p : n
    })
    onApply(parsed)
  }

  return (
    <div
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
      className="absolute left-0 top-full z-20 mt-0.5 flex items-center gap-1 rounded-md border border-primary/15 bg-popover px-2 py-1.5 shadow-lg shadow-black/40"
    >
      {localParams.map((val, i) => (
        <input
          key={i}
          type="number"
          value={val}
          onChange={(e) => {
            const next = [...localParams]
            next[i] = e.target.value
            setLocalParams(next)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleApply()
          }}
          className="h-[22px] w-11 select-text rounded-sm border border-border bg-field px-1 text-center text-[11px] text-[hsl(var(--chart-text))] outline-hidden"
        />
      ))}
      <button
        onClick={handleApply}
        className="h-[22px] whitespace-nowrap rounded-sm bg-primary px-2 text-[10px] font-semibold text-primary-foreground hover:bg-primary-hover"
      >
        Apply
      </button>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  LegendButton                                                       */
/* ------------------------------------------------------------------ */

function LegendButton({
  children,
  title,
  onClick,
  active = false,
  pressed,
}: {
  children: React.ReactNode
  title?: string
  onClick: (e: React.MouseEvent) => void
  /** Toggle-button look: when active, the icon stays highlighted (not just on
   *  hover) so a "pinned" state reads at a glance. */
  active?: boolean
  /** Toggle-button semantics: when defined, the button is announced to
   *  assistive tech as a toggle via `aria-pressed`. Omit for plain action
   *  buttons (settings / remove / move / locate) so they stay normal buttons. */
  pressed?: boolean
}) {
  return (
    <button
      title={title}
      aria-pressed={pressed}
      onClick={onClick}
      className={
        'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-sm p-0 transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))] ' +
        (active ? 'bg-accent' : '') +
        ' text-[hsl(var(--chart-text))]'
      }
    >
      {children}
    </button>
  )
}

/* ------------------------------------------------------------------ */
/*  Icons (12x12)                                                      */
/* ------------------------------------------------------------------ */

/** Lucide's `loader-circle` glyph, inlined (charts-core carries no icon-set dependency,
 *  see PositionSizeReadout), spinning. 11x11 like the timeframe cue it sits beside. */
function LoaderIcon() {
  return (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="animate-spin motion-reduce:animate-none"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

function EyeIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

function GearIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  )
}

/** Crosshair / locate — "show me where this series comes from". */
function LocateIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="7" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
    </svg>
  )
}

/** Push-pin / thumbtack — "pin to price". A thumbtack silhouette (deliberately
 *  NOT a map-pin — its inner circle read too close to the locate crosshair);
 *  fills solid when the row is pinned so the active state reads at a glance. */
function PinIcon({ filled = false }: { filled?: boolean }) {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </svg>
  )
}

function ArrowUpIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  )
}

function ArrowDownIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}
